
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowLeft, Zap, Users, Trophy, BookOpen } from 'lucide-react';

const steps = [
  {
    title: 'Create Account',
    description: 'Sign up with your email. Select your target subjects (Domain + Language + General Test).',
    icon: <Users className="h-6 w-6 text-blue-500" />
  },
  {
    title: 'Take Free Mock',
    description: 'Attempt 1 full-length mock test completely free. Experience the real NTA interface.',
    icon: <Zap className="h-6 w-6 text-amber-500" />
  },
  {
    title: 'Check Leaderboard',
    description: 'See your All-India Rank instantly. Compare your score with thousands of students.',
    icon: <Trophy className="h-6 w-6 text-purple-500" />
  },
  {
    title: 'Upgrade & Master',
    description: 'Go Pro/Premium for 8–10 mocks per subject per month, weak-area analysis, and AI-driven improvement.',
    icon: <BookOpen className="h-6 w-6 text-green-500" />
  }
];

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 py-4 sticky top-0 bg-white/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5 text-gray-600 mr-2" />
            <img src="/logo-full.png" alt="Crack CUET" className="h-12" />
          </div>
          <Button onClick={() => navigate('/auth')}>Login</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-extrabold text-primary mb-4">How Crack CUET Works</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A simple 4-step process to maximize your CUET score.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20 relative">
          {/* Connector Line (Desktop) */}
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-gray-200 -z-10"></div>

          {steps.map((step, index) => (
            <div key={index} className="flex flex-col items-center text-center bg-white p-4">
              <div className="w-24 h-24 rounded-full bg-white border-4 border-gray-100 flex items-center justify-center mb-6 shadow-sm z-10">
                {step.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600">{step.description}</p>
            </div>
          ))}
        </div>

        {/* Pricing Update Section */}
        <div className="bg-section rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <Badge className="bg-green-100 text-green-700 border-0 mb-4 px-4 py-1">New Pricing</Badge>
            <h2 className="text-3xl font-bold text-primary mb-4">Unbeatable Value</h2>
            <p className="text-gray-600">We've slashed our prices to make top-tier prep accessible to everyone.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free */}
            <Card className="border-2 border-gray-200">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Free</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">₹0</span>
                </div>
                <CardDescription className="mt-2">Forever</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> 1 Free Mock Total</li>
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> Basic Analytics</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full" onClick={() => navigate('/auth')}>Start Free</Button>
              </CardFooter>
            </Card>

            {/* Pro */}
            <Card className="border-2 border-blue-500 shadow-lg relative bg-white">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">Most Popular</div>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-blue-600">Pro</CardTitle>
                <div className="mt-4">
                  <span className="text-gray-400 line-through mr-2">₹999</span>
                  <span className="text-4xl font-bold text-gray-900">₹699</span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
                <CardDescription className="mt-2">Serious Aspirants</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> 8 mocks per subject/month</li>
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> Full Leaderboard</li>
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> Weak-Topic Analysis</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/auth?redirect=/upgrade')}>Go Pro</Button>
              </CardFooter>
            </Card>

            {/* Premium */}
            <Card className="border-2 border-amber-400 shadow-lg bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-amber-600">Premium</CardTitle>
                <div className="mt-4">
                  <span className="text-gray-400 line-through mr-2">₹1499</span>
                  <span className="text-4xl font-bold text-gray-900">₹999</span>
                  <span className="text-gray-500 text-sm">/month</span>
                </div>
                <CardDescription className="mt-2">Top Rankers</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> 10 mocks per subject/month</li>
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> Rank Prediction</li>
                  <li className="flex items-center"><Check className="h-4 w-4 text-green-500 mr-2" /> Detailed Solutions</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate('/auth?redirect=/upgrade')}>Go Premium</Button>
              </CardFooter>
            </Card>
          </div>
          <div className="mt-10 text-center text-sm text-gray-400">
            Any questions about plans or payments? Email us at{' '}
            <a href="mailto:info@crackcuet.com" className="text-blue-500 hover:underline">info@crackcuet.com</a>
          </div>
        </div>
      </main>
    </div>
  );
}
